import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, useParams, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { LotProvider, useLot } from '../contexts/LotContext';
import { useUser } from '../contexts/UserContext';
import Chat, { AGENT_ACTION_APPLIED_EVENT } from './ai/Chat';
import { lotService } from '../utils/api';
import { isSuperadminModeEnabled } from './SuperadminHeader';
import { MultiLotProvider } from '../contexts/MultiLotContext';

const getIsMobile = () => window.matchMedia('(max-width: 480px)').matches;

// Minimum content width before chat should stop expanding
const MIN_CONTENT_WIDTH = 600;

const CHAT_ENABLED_ROUTES = [
    '/dashboard',
    '/occupants',
    '/registry',
    '/violators',
    '/notifications',
    '/settings',
    '/advanced',
    '/users',
    '/parallel-ai'
];

const LotLayoutContent: React.FC<{ lotId: string }> = ({ lotId }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { logout, userLots, user } = useUser();
    const { lotData } = useLot();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isMobile, setIsMobile] = React.useState(getIsMobile());
    const [isMobileChatOpen, setIsMobileChatOpen] = useState(false);
    const [hasFullAccess, setHasFullAccess] = useState(false);
    const [chatMaxWidth, setChatMaxWidth] = useState<number | undefined>(undefined);
    const [pageRefreshKey, setPageRefreshKey] = useState(0);
    const lotMainRef = useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const checkMobile = () => setIsMobile(getIsMobile());
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        const handleAgentApplied = () => {
            setPageRefreshKey(prev => prev + 1);
        };
        window.addEventListener(AGENT_ACTION_APPLIED_EVENT, handleAgentApplied);
        return () => window.removeEventListener(AGENT_ACTION_APPLIED_EVENT, handleAgentApplied);
    }, []);

    // Calculate max chat width based on available space
    const calculateChatMaxWidth = useCallback(() => {
        if (!lotMainRef.current || isMobile) {
            setChatMaxWidth(undefined);
            document.body.classList.remove('layout-condensed');
            return;
        }
        const lotMainWidth = lotMainRef.current.offsetWidth;
        // Available space = lot-main width - minimum content width
        // This ensures content always has at least MIN_CONTENT_WIDTH
        const availableForChat = lotMainWidth - MIN_CONTENT_WIDTH;
        setChatMaxWidth(availableForChat > 0 ? availableForChat : undefined);

        // Toggle body class for condensed layout (used by fixed-position elements like tutorial button)
        const contentWidth = lotMainWidth - (chatMaxWidth ?? 230);
        if (contentWidth < 800) {
            document.body.classList.add('layout-condensed');
        } else {
            document.body.classList.remove('layout-condensed');
        }
    }, [isMobile, chatMaxWidth]);

    useEffect(() => {
        const element = lotMainRef.current;
        if (!element) return;

        const observer = new ResizeObserver(() => {
            calculateChatMaxWidth();
        });

        observer.observe(element);
        calculateChatMaxWidth();

        return () => observer.disconnect();
    }, [calculateChatMaxWidth]);

    React.useEffect(() => {
        if (!isMobile) {
            setIsMobileChatOpen(false);
        }
    }, [isMobile]);

    useEffect(() => {
        let isCancelled = false;

        const determineAccess = async () => {
            if (!lotData || !user) {
                setHasFullAccess(false);
                return;
            }

            if (isSuperadminModeEnabled()) {
                setHasFullAccess(true);
                return;
            }

            if (lotData.ownerId === user.userId) {
                setHasFullAccess(true);
                return;
            }

            try {
                const operators = await lotService.getOperators(lotId);
                if (!isCancelled) {
                    const isOperator = operators.some((op: any) => op.userId === user.userId);
                    setHasFullAccess(isOperator);
                }
            } catch {
                if (!isCancelled) {
                    setHasFullAccess(false);
                }
            }
        };

        determineAccess();

        return () => {
            isCancelled = true;
        };
    }, [lotData, user, lotId]);

    const lotName = React.useMemo(() => {
        if (!userLots || !lotId) return '';
        const lot = userLots.find(l => l.lotId === lotId);
        return lot ? lot.lotName : '';
    }, [userLots, lotId]);

    const truncatedLotName = lotName.length > 18 ? lotName.substring(0, 18) + '…' : lotName;
    const hasMultipleLots = userLots && userLots.length > 1;

    const pageTitle = React.useMemo(() => {
        const path = location.pathname;
        if (path.includes('/superadmin/cameras')) return 'Cameras';
        if (path.includes('/superadmin/alpr-log')) return 'ALPR';
        if (path.includes('/superadmin/sessions')) return 'Sessions';
        if (path.includes('/superadmin/disputes')) return 'Disputes';
        if (path.includes('/users')) return 'Users';
        if (path.includes('/dashboard')) return 'Dashboard';
        if (path.includes('/occupants')) return 'Occupants';
        if (path.includes('/registry')) return 'Registry';
        if (path.includes('/violators')) return 'Violators';
        if (path.includes('/notifications')) return 'Notifications';
        if (path.includes('/parallel-ai')) return 'Parallel AI';
        if (path.includes('/settings')) return 'Settings';
        if (path.includes('/advanced')) return 'Advanced';
        if (path.includes('/addons')) return 'Addons';
        return 'Dashboard';
    }, [location.pathname]);

    const handleLogout = async () => {
        await logout();
    };

    const isOnChatEnabledRoute = CHAT_ENABLED_ROUTES.some(route =>
        location.pathname.includes(route)
    );

    const showChat = isOnChatEnabledRoute && hasFullAccess;

    const isOnParallelAIPage = location.pathname.includes('/parallel-ai');

    React.useEffect(() => {
        if (!showChat) {
            setIsMobileChatOpen(false);
        }
    }, [showChat]);

    return (
        <div className="app-container">
            <Sidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />
            <div ref={lotMainRef} className={`lot-main${showChat ? ' has-chat' : ''}`}>
                <div className="lot-main__content-shell">
                    <div className="content">
                        {isMobile && (
                            <div className="mobile-header">
                                <div className="mobile-header-left">
                                    <button
                                        className="sidebar-hamburger"
                                        aria-label="Open Sidebar"
                                        onClick={() => setIsSidebarOpen(true)}
                                    >
                                        <img src="/assets/nav/menu.svg" alt="Open Sidebar" />
                                    </button>
                                </div>
                                <div className="mobile-header-center">
                                    {hasMultipleLots ? (
                                        <span
                                            className="mobile-lotname-link"
                                            tabIndex={0}
                                            role="button"
                                            aria-label="Change Lot"
                                            onClick={() => navigate('/lot-dashboard')}
                                            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigate('/lot-dashboard'); }}
                                        >
                                            {truncatedLotName}
                                        </span>
                                    ) : (
                                        <span className="mobile-lotname-link">{truncatedLotName}</span>
                                    )}
                                    <img
                                        src="/assets/nav/arrow_right.svg"
                                        alt=""
                                        aria-hidden="true"
                                    />
                                    <span>{pageTitle}</span>
                                </div>
                                <div className="mobile-header-right">
                                    {showChat ? (
                                        <button
                                            className={`mobile-chat-toggle${isMobileChatOpen ? ' is-active' : ''}`}
                                            aria-label={isMobileChatOpen ? 'Close Parallel AI chat' : 'Open Parallel AI chat'}
                                            onClick={() => setIsMobileChatOpen(prev => !prev)}
                                        >
                                            <img src="/assets/ai/ChatSmall.svg" alt="Toggle Parallel AI chat" />
                                        </button>
                                    ) : (
                                        <button
                                            className="mobile-logout"
                                            aria-label="Logout"
                                            onClick={handleLogout}
                                        >
                                            <img src="/assets/nav/Logout.svg" alt="Logout" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                        <Outlet key={pageRefreshKey} />
                    </div>
                </div>
                {showChat && (
                    <Chat
                        isMobile={isMobile}
                        isMobileOpen={isMobileChatOpen}
                        onMobileVisibilityChange={setIsMobileChatOpen}
                        defaultOpen={isOnParallelAIPage}
                        maxWidth={chatMaxWidth}
                    />
                )}
            </div>
        </div>
    );
};

const LotLayout = () => {
    const { lotId } = useParams<{ lotId: string }>();

    if (!lotId) {
        return <div>Lot ID not found.</div>;
    }

    return (
        <MultiLotProvider>
            <LotProvider lotId={lotId}>
                <LotLayoutContent lotId={lotId} />
            </LotProvider>
        </MultiLotProvider>
    );
};

export default LotLayout;
